import { MouseEventHandler, useEffect, useRef, useState } from "react"

import lowerCase from "lodash/lowerCase"
import ReactDOM from "react-dom"
import { useSelector } from "react-redux"

import { SvgIcon } from "@components/common"
import { selectUserId } from "@selectors/user"
import { showAiFeature } from "@utils/showAiFeature"

import { useSuggestionInPersonStore } from "stores/suggestions/suggestionInPersonStore"

import { useTeeUpPage } from "../../../pages/TeeUpPage/TeeUpPageContext"
import getWhenSuggestionDetails from "../../../utils/gameplan/getWhenSuggestionDetails"
import getWhereSuggestionDetails from "../../../utils/gameplan/getWhereSuggestionDetails"
import { subscribeGameplanFocus } from "../../../utils/gameplanFocusBus"
import SuggestionPopup from "../../gamePlansCard/SuggestionPopup"
import { useGameplans } from "../hooks"

// TEEUP-LIFECYCLE fix (2026-07-04, QJ ruling, screenshot-verified problem, CORRECTED per QJ
// follow-up): this remains ONE half-width column inside GameplanSection's two-column `.gp2-row`
// (side-by-side geometry stays -- QJ: stacking "causes navigation challenges"). What changed is
// the expanded content's HOME: it used to mount its own GameplanBlock nested inside this column
// (`{isExpanded && <GameplanBlock type={type} />}`), which duplicated this row's own
// label/decided-value (GameplanBlock rendered GameplanHeader + DecidedSuggestion on top of
// SuggestionsList) AND squeezed that duplicated content into a half-width column -- the reported
// "Where address renders twice" / squeezed-heights bug. GameplanBlock's expanded rendering now
// lives in GameplanSection.tsx as a FULL-WIDTH panel below the two-column header row, so this
// component only owns the COLLAPSED row + its own expand/collapse control; `isExpanded` and
// `setExpanded` are lifted to GameplanSection so When and Where still expand independently (no
// more single shared toggle for both columns).
const WhenWhereCard = ({
  type,
  isExpanded,
  setExpanded,
}: {
  type: string
  isExpanded: boolean
  setExpanded: (value: boolean) => void
}) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const { teeup, openSuggestWhenAiModal } = useTeeUpPage()
  const lowerType = lowerCase(type)
  const isWhen = lowerType === "when"
  // C2-U6b3 FOLLOW-UP (2026-07-11, U7 live-audit finding): WhenWhereCard.tsx is the component
  // ACTUALLY rendered on the TeeUp detail page (superseded atoms/GameplanHeader.js's collapsed
  // row per the 2026-07-04 TEEUP-LIFECYCLE refactor, see the file-header comment above) -- the
  // original C2-U6b3 grounding assumed GameplanHeader/SuggestionsActions were still the live
  // "AI suggest" trigger, which was stale by the time this chain ran. Same gate GameplanHeader.js
  // itself uses (isWhen only, kill-switched by showAiFeature) -- WHERE stays manual-only, exactly
  // as the plan's own dev-only-empty-state gating already established.
  const userId = useSelector(selectUserId)
  const showAi = isWhen && showAiFeature(userId)
  const { suggestions, decidedSuggestion } = useGameplans({
    teeup,
    type: lowerType,
    isNewest: true,
  })

  const setIsWhenModalOpen = useSuggestionInPersonStore(
    state => state.setIsWhenModalOpen
  )
  const setIsWhereModalOpen = useSuggestionInPersonStore(
    state => state.setIsWhereModalOpen
  )

  // CARD-INTERACTIONS fix (2026-07-04, QJ bug: once a dimension is decided there is no visible
  // way to change it -- the iPhone gold standard (teeup-mobile suggestionSection.js/
  // suggestionHeader.js) makes the decided/top VALUE itself clickable, opening the same
  // SuggestionPopup the expanded list already uses (SuggestionPopup already offers
  // "Set as Undecided" via usePopupActions.js + an "Other suggestions" tab -- see
  // src/components/gamePlansCard/SuggestionPopup/hooks/usePopupActions.js line ~56-96, index.js
  // line ~136-137). Ported the exact open-mechanism DecidedSuggestion.js already uses elsewhere
  // in this file tree (local isOpen state + ReactDOM.createPortal into #modal-root) rather than
  // inventing a new one. State generalized below (popupSuggestion) once the pill strip gave
  // every suggestion, not just one, its own click-through popup.

  // Inline "game-plan moment" rows in the Conversation (e.g. "Maya suggested a time") scroll to
  // AND expand the shared card -- never navigates away (spec).
  useEffect(() => {
    return subscribeGameplanFocus(focusedType => {
      if (focusedType !== lowerType) return
      setExpanded(true)
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowerType])

  const label = isWhen ? "When" : "Where"
  const getDetails = isWhen ? getWhenSuggestionDetails : getWhereSuggestionDetails

  // ROOT CAUSE (2026-07-03, overlapping-When-conflict ticket): this collapsed card is the
  // component that ACTUALLY mounts on /teeUps/{id} (TeeupPageLeftSection -> GameplanSection ->
  // WhenWhereCard), rendered BEFORE any user interaction. The two prior fixes (useGameplans.js
  // computing isCalendarConflict, SuggestionItem.js's renderConflictLabel) only wired the
  // EXPANDED list (GameplanBlock -> SuggestionsList -> SuggestionItem, reached only after
  // clicking the expand toggle) -- this collapsed-pill render path never read
  // suggestion.isCalendarConflict at all; `pills` discarded everything but the title string, so
  // there was no data left to key a conflict indicator off. Keep the flag alongside the title.
  //
  // PILLS-PARITY fix (2026-07-04, QJ ruling): no artificial `.slice(0, 6)` cap here -- the
  // iPhone gold standard (suggestionSection.js renderAlternativeSuggestions ~55-74) renders
  // EVERY alternative suggestion into a horizontally-scrolling FlatList, no "+N more" affordance;
  // the horizontal scroll strip below is the overflow mechanism, not a truncated list.
  const pillData = (suggestions || [])
    .filter(s => !s?.isWithdrawn)
    .map(s => {
      const details = getDetails(s as any)
      if (!details?.title) return null
      return {
        title: details.title,
        isCalendarConflict: isWhen && !!(s as any)?.isCalendarConflict,
        suggestion: s,
      }
    })
    .filter(Boolean) as { title: string; isCalendarConflict: boolean; suggestion: unknown }[]

  const decidedDetails = decidedSuggestion ? getDetails(decidedSuggestion as any) : null
  const decidedConflict =
    isWhen && !decidedDetails ? false : isWhen && !!(decidedSuggestion as any)?.isCalendarConflict
  const primaryConflict = decidedDetails ? decidedConflict : false
  const hasSuggestions = pillData.length > 0

  // COPY fix (2026-07-04, QJ ruling 1): "Awaiting a decision" was invented editorial copy (not
  // product language) -- the suggestion-mode row now shows the suggestions themselves (the pill
  // strip + count, below) instead of a caption. Only the DECIDED state still has a sub-line, and
  // it is real data (the decided suggestion's own subtitle/date), not commentary.
  const primaryValue = decidedDetails?.title || `No ${label.toLowerCase()} yet`
  const subLine = decidedDetails ? decidedDetails.subtitle : ""

  const suggestHandle = () => {
    isWhen ? setIsWhenModalOpen(true) : setIsWhereModalOpen(true)
  }

  // Per-state click map (iPhone gold standard, suggestionSection.js redirectToSuggestions
  // ~121-131 + row TouchableOpacity ~135; suggestionHeader.js handlePressHeader ~195-206 +
  // contentWrapper TouchableOpacity ~222):
  //   EMPTY (no suggestions, not decided)      -> row click opens the Suggest flow (same as the
  //                                                "Suggest" pill).
  //   SUGGESTIONS, NONE DECIDED                -> row click TOGGLES this section's own expand
  //                                                state (the sibling chevron button does the
  //                                                same); each pill's OWN click instead opens ITS
  //                                                popup (stopPropagation so it doesn't also
  //                                                trigger the row's expand toggle).
  //   DECIDED                                  -> the decided VALUE click opens the decided
  //                                                suggestion's popup (Set as Undecided lives
  //                                                there, not on the collapsed card); matches the
  //                                                iPhone gold standard exactly -- mobile's
  //                                                renderAlternativeSuggestions/redirectToSuggestions
  //                                                both no-op once a gameplan is decided, so a
  //                                                decided row has no expand affordance at all.
  // TEEUP-LIFECYCLE fix (2026-07-04): expand is now a per-column TOGGLE (open AND close), not
  // expand-only -- the prior build relied on a single shared bottom toggle (now removed, see
  // GameplanSection.tsx) to collapse either column; each column now owns both directions of its
  // own expand state via the lifted isExpanded/setExpanded props.
  const rowClickHandler = decidedDetails
    ? undefined
    : hasSuggestions
      ? () => setExpanded(!isExpanded)
      : suggestHandle
  // PILLS-PARITY fix (2026-07-04): state 2 now renders every suggestion as its own pill, so the
  // popup target is whichever pill was clicked, not a single fixed "top suggestion". Decided
  // state keeps a single target (the decided suggestion). Generalized from a boolean +
  // fixed-topSuggestion pair to a suggestion-object state so both cases share one mechanism.
  const [popupSuggestion, setPopupSuggestion] = useState<unknown>(null)
  const openSuggestionPopup =
    (suggestion: unknown): MouseEventHandler =>
    e => {
      if (!suggestion) return
      e.stopPropagation()
      setPopupSuggestion(suggestion)
    }

  // Ale `.gp2-col` (padding: 16px 18px), `.gp2-head` (flex justify-between gap 8px),
  // `.gp2-primary` (15px/500/var(--ink) #161A22; `.sug` -> var(--st-suggest) #2F6FED;
  // `.none` -> var(--st-plan) #ED9B12), `.gp2-sub` (13px, var(--muted-solid) #9AA2B1,
  // margin-top 4px), `.gp-suggest` (solid pill: bg var(--ink) #161A22, white text, radius
  // 999px, padding 7px 17px, 13px/500) all ported verbatim.
  return (
    // PRE-ALE-MATCH (2026-07-04, QJ correction): equal padding (the GAMEPLAN-POLISH `mt-1`/
    // `pb-1` rhythm fix, 2026-07-04 23:43 ET row) was not enough -- the three states (empty /
    // suggestions / decided) still render a different NUMBER of content lines (2 / 3 / up-to-4),
    // so matching only the per-line spacing still let the row's total height float with its
    // state. `min-h-[120px]` reserves room for the tallest normal state measured live across
    // real fixtures (the suggestions-state pill strip measured 116.5px on teeUps/100006 and
    // teeUps/100011, the tallest of the three states -- decided and empty both measured <=112px)
    // plus a small rounding buffer, so WHEN and WHERE clamp to an IDENTICAL height regardless of
    // which of the three states each is independently in -- content stays top-aligned
    // (`items-start` on the row below, natural top-anchored block flow here) so shorter states
    // just carry extra bottom whitespace inside the shared box, exactly like the mobile
    // prototype's two matched-size Game-plan halves.
    <div
      ref={cardRef}
      data-testid={`teeup-detail-${lowerType}-card`}
      className="min-h-[120px] min-w-0 px-[18px] py-4 scroll-mt-6">
      <div className="flex items-start justify-between gap-2">
        <div
          data-testid={`teeup-detail-${lowerType}-row`}
          onClick={rowClickHandler}
          className={`min-w-0 flex-1 ${rowClickHandler ? "cursor-pointer" : ""}`}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA2B1]">
            {label}
          </span>

          {/* CANONICAL-DETAIL (2026-07-04, QJ FINAL RULING): reinstates a green "Decided" state
              chip on the Game plan WHEN/WHERE rows -- the mobile Figma prototype's ("Product
              RoadMap Meeting", All-set state) treatment. This SUPERSEDES the 2026-07-03
              DECIDED-BADGE-CLEANUP removal (standing rule 16, "no user-visible Decided badge")
              for THIS slot only -- that rule still bans the word/badge everywhere else (action
              narratives, status labels, etc). Colors per QJ-RULINGS-CREATE-FLOW-2026-07-04.md
              Ruling 5, the token-true beta green: text #15803D (same value already carried by
              this codebase's `--green3` token, SuggestionItem/atoms/StatusLabel.js) on #F0FDF4. */}
          {decidedDetails && (
            <span
              data-testid={`teeup-detail-${lowerType}-decided-chip`}
              className="ml-1.5 inline-flex items-center rounded-full bg-[#F0FDF4] px-2 py-0.5 align-middle text-[11px] font-semibold text-[#15803D]">
              Decided
            </span>
          )}

          {decidedDetails && (
            <>
              <div
                data-testid={`teeup-detail-${lowerType}-value`}
                onClick={openSuggestionPopup(decidedSuggestion)}
                className="mt-1 cursor-pointer truncate text-[15px] font-medium text-[#161A22] transition-opacity hover:opacity-75">
                {primaryValue}
              </div>

              {primaryConflict && (
                <div
                  data-testid={`teeup-detail-${lowerType}-value-conflict`}
                  className="mt-1 truncate text-[12px] font-medium text-[#ef4444]">
                  &#8226; Calendar conflict
                </div>
              )}

              {subLine && (
                <div className="mt-1 min-h-[18px] truncate text-[13px] text-[#9AA2B1]">
                  {subLine}
                </div>
              )}
            </>
          )}

          {!decidedDetails && !hasSuggestions && (
            <div
              data-testid={`teeup-detail-${lowerType}-value`}
              className="mt-1 truncate text-[15px] font-medium text-[#ED9B12]">
              {primaryValue}
            </div>
          )}

          {/* PILLS-PARITY fix (2026-07-04, QJ ruling 3): state 2 (suggestions, none decided) no
              longer shows one "primary" suggestion as plain text plus the rest wrapped below a
              divider -- it shows EVERY suggestion as an equal pill in a horizontally-scrolling
              strip, matching the iPhone gold standard (suggestionSection.js
              renderAlternativeSuggestions ~55-74: horizontal snap FlatList, shown only when
              suggestions exist and none decided) plus its count line (suggestionHeader.js
              ~48-58: "{n} suggestion(s)", a plain count -- never reaction/vote tallies). Each
              pill keeps its own click-through to SuggestionPopup (stopPropagation so it doesn't
              also fire the row's expand-on-click) and keeps the conflict-red styling from
              afde3a67 where applicable. */}
          {!decidedDetails && hasSuggestions && (
            <>
              <div
                data-testid={`teeup-detail-${lowerType}-suggestions-count`}
                className="mt-1 truncate text-[13px] text-[#9AA2B1]">
                {pillData.length} suggestion{pillData.length > 1 ? "s" : ""}
              </div>

              {/* Scrollbar fix (2026-07-04, QJ ruling 4): app-wide default (src/index.scss) is
                  already a slim 8px overlay-style scrollbar (not a fat native one), but this
                  strip is a small in-card widget, so it gets the same PER-WIDGET thinner
                  override this codebase already uses for compact chip/tag strips (see
                  src/components/TagUsersModal/components/expandedTags.scss `.tag-list`: 4px,
                  light thumb, no track shading) rather than inventing a new treatment.

                  ROW-RHYTHM fix (2026-07-04, QJ defect: WHEN/WHERE rows read as unevenly spaced
                  in the stacked canonical card): this was `mt-2 ... pb-1.5`, the only line in
                  either row's content stack that broke the shared `mt-1` label->content->footer
                  gap every other line here uses (the count line above is `mt-1`; the decided
                  branch's value/conflict/subLine lines below are all `mt-1` too) and the only
                  line that added its OWN extra bottom padding beyond the row's outer `py-4` --
                  together those made the suggestions-state row measurably taller inside the same
                  outer padding than the decided-state row, which is the "uneven rows" QJ saw.
                  `mt-1` restores the shared rhythm; `pb-1` (not 0) keeps a hairline of clearance
                  so the thin scrollbar thumb never touches a pill's rounded bottom edge, while
                  staying small enough not to reintroduce a visible height delta against the
                  decided row's plain trailing line. */}
              <div
                data-testid={`teeup-detail-${lowerType}-pills`}
                onClick={e => e.stopPropagation()}
                className="mt-1 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto overflow-y-hidden pb-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#d6d6d6] [&::-webkit-scrollbar-track]:bg-transparent">
                {pillData.map((p, i) => (
                  <span
                    key={`${p.title}-${i}`}
                    data-testid={`teeup-detail-${lowerType}-pill`}
                    onClick={openSuggestionPopup(p.suggestion)}
                    // SIZING fix (2026-07-04, QJ bug: Suggest pill overflows the card box edges)
                    // carried forward: `flex-none` + `max-w-[220px]` + `truncate` keeps a pill
                    // from ever growing past a sane width inside the scroll strip (the strip
                    // itself, not `min-w-0` shrinking, is now the overflow mechanism).
                    className={`flex-none max-w-[220px] cursor-pointer truncate rounded-full border px-2.5 py-1 text-[12px] font-medium transition-opacity hover:opacity-75 ${
                      p.isCalendarConflict
                        ? "border-[#f5c2c2] bg-[#fdecec] text-[#ef4444]"
                        : "border-[#DCE3ED] bg-[#F0F3F8] text-[#586173]"
                    }`}>
                    {p.isCalendarConflict && <b>&#8226; </b>}
                    {p.title}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* DECIDE-CTA-DEMOTE fix (2026-07-04, QJ bug: decided card still screamed the big black
            "Suggest" pill next to the decided value, read as "decide doesn't work"). Backend
            decide already worked -- this was purely presentation, and it was wrong in BOTH
            non-empty states, not just decided. Ground truth is the beta/mobile card's own 3-state
            machine (F:\AI_Data\CODE-REPOS\teeup-mobile\app\components\teeup\gameplansCard\
            gameplansCardItems\suggestionHeader.js renderRightHeaderPart ~104-157):
              state 1 (no suggestions yet)      -> plain "Suggest" CTA (the only state that has one)
              state 2 (suggestions, none decided) -> renderRightHeaderPart returns nothing: NO
                CTA on the card; suggesting more happens from the expanded list (web equivalent:
                the expand toggle -> SuggestionsActions.js's always-present "+ Suggest {type}")
              state 3 (decided)                 -> decided indicator only (icon/value styling);
                mobile shows a literal "Decided" text here but that reproduces the banned "Decided"
                badge (QJ standing rule, already kept out of DecidedSuggestion.js) -- icon/value
                treatment only, no CTA and no "Decided" text on web.
            Capability to suggest more is never removed, only reachable via expand (setExpanded ->
            GameplanSection's full-width GameplanBlock panel -> SuggestionsList ->
            SuggestionsActions), matching mobile's own suggestionDetails-screen path for states 2
            and 3. */}
        {/* SUGGEST-PILL-COLOR fix (2026-07-05, QJ): this pill was solid black (`#161A22`, ported
            verbatim from Ale's `.gp-suggest`), which reads as a different, disconnected control
            from the rest of the suggestion UI. Brand palette is `#7FB1E3` per the design tokens
            (matching the blue suggestion badges already shipped elsewhere on this card) --
            presentation-only change, click behavior (suggestHandle) unchanged. */}
        {!decidedDetails && !hasSuggestions && showAi && (
          <button
            type="button"
            data-testid="aiSuggestWhenGameplanHeader"
            onClick={openSuggestWhenAiModal}
            className="flex-none whitespace-nowrap rounded-full border border-[#7FB1E3] bg-white px-[17px] py-[7px] text-[13px] font-medium text-[#7FB1E3] hover:opacity-90">
            AI suggest
          </button>
        )}
        {!decidedDetails && !hasSuggestions && (
          <button
            type="button"
            data-testid={`teeup-detail-${lowerType}-suggest`}
            onClick={suggestHandle}
            className="flex-none whitespace-nowrap rounded-full bg-[#7FB1E3] px-[17px] py-[7px] text-[13px] font-medium text-white hover:opacity-90">
            Suggest
          </button>
        )}

        {/* TEEUP-LIFECYCLE fix (2026-07-04): per-column expand/collapse chevron, replacing the
            single shared bottom toggle GameplanSection used to own for BOTH columns at once. Only
            state 2 (suggestions, none decided) gets one -- state 3 (decided) has no expand
            affordance at all on the iPhone gold standard (see click-map comment above), and state
            1 (empty) has nothing to expand. The panel it reveals now renders full-width in
            GameplanSection, not squeezed into this half-column (see that file). */}
        {!decidedDetails && hasSuggestions && (
          <button
            type="button"
            data-testid={`teeup-detail-${lowerType}-expand-toggle`}
            aria-label={isExpanded ? `Collapse ${label} details` : `Expand ${label} details`}
            onClick={() => setExpanded(!isExpanded)}
            className="flex-none rounded-full p-1 text-[#9AA2B1] transition-colors hover:text-[#586173]">
            <SvgIcon
              name="arrow-2"
              color="currentColor"
              className={isExpanded ? "!rotate-180" : ""}
            />
          </button>
        )}

        {/* CANONICAL-DETAIL (2026-07-04, QJ FINAL RULING): a calendar/action icon at the right
            of a DECIDED row, per the mobile Figma prototype's All-set state (each decided WHEN/
            WHERE row carries a calendar glyph). Reuses the same popup this row's decided value
            already opens on click (openSuggestionPopup) -- no new backend call invented; a
            decided row otherwise has no right-side control at all (see the click-map comment
            above), so this restores a reachable affordance there without adding new behavior. */}
        {decidedDetails && (
          <button
            type="button"
            data-testid={`teeup-detail-${lowerType}-calendar-action`}
            aria-label={`${label} details`}
            onClick={openSuggestionPopup(decidedSuggestion)}
            className="flex-none rounded-full p-1 text-[#9AA2B1] transition-colors hover:text-[#586173]">
            <SvgIcon name="calendar" color="currentColor" />
          </button>
        )}
      </div>

      {!!popupSuggestion &&
        ReactDOM.createPortal(
          <SuggestionPopup
            currentSuggestion={popupSuggestion}
            currentSuggestionData={popupSuggestion}
            toggleSuggestionPopup={() => setPopupSuggestion(null)}
          />,
          document.querySelector("#modal-root") as Element
        )}
    </div>
  )
}

export default WhenWhereCard
